import { DISPLAY_TYPES } from 'Editor/services/consts';
import { EditorDOMElements, EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { BaseViewElement } from './BaseViewElement';

export class BaseBlockElement extends BaseViewElement {
  private options: Editor.Elements.BaseBlockElementOptions;
  protected isLocked: boolean;
  protected userIdLocking: string;
  protected lockTimeout?: NodeJS.Timeout | null = null;

  constructor(
    Visualizer?: Editor.Visualizer.State,
    Data?: Editor.Data.API,
    options: Editor.Elements.BaseBlockElementOptions = { doNeedBaseTooltip: true },
  ) {
    super(Visualizer, Data);
    this.isLocked = false;
    this.userIdLocking = '';
    this.options = options;
    if (this.options.doNeedBaseTooltip) {
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
    }
  }

  static get observedAttributes() {
    return ['lock'];
  }

  attributeChangedCallback(attribute: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      switch (attribute) {
        case 'lock':
          this.handleLockStatus();
          break;
        default:
          break;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.options.doNeedBaseTooltip) {
      this.destroyLockTooltip();
    }
  }

  private handleLockStatus() {
    const lock = this.getAttribute('lock');
    const nodeId = this.getAttribute('id');

    let nodeData: Editor.Data.Node.Model | undefined = undefined;
    if (nodeId) {
      nodeData = this.Data?.nodes.getNodeModelById(nodeId);
    }
    if (lock != null && lock !== 'false') {
      this.isLocked = true;

      if (typeof lock === 'string' && lock !== 'true') {
        // locked from server operation

        // set tooltip instance
        this.initializeLockTooltip();

        // FAILSAFE: set timeout to unlock (only when lock comes from server)
        if (this.lockTimeout) {
          clearTimeout(this.lockTimeout);
          delete this.lockTimeout;
        }
        this.lockTimeout = setTimeout(() => {
          this.removeAttribute('lock');
          nodeData?.setArtificialLock();
          delete this.lockTimeout;
        }, 6000);
      } else if (lock === 'true') {
        // locked from client - readonly
        this.destroyLockTooltip();
      }
    } else {
      this.isLocked = false;
      nodeData?.setArtificialLock();

      this.destroyLockTooltip();
      if (this.lockTimeout) {
        clearTimeout(this.lockTimeout);
        delete this.lockTimeout;
      }
    }
  }

  hasTasks() {
    return this.hasAttribute('task');
  }

  getTasks() {
    return this.getAttribute('task')?.split(',') ?? [];
  }

  //TODO
  validateOnlyChildState() {}

  preRender() {
    super.preRender();
  }

  set selectedTask(value: boolean) {
    if (value) {
      this.setAttribute('selectedTask', 'true');
    } else {
      this.removeAttribute('selectedTask');
    }
  }

  selectTask() {
    this.selectedTask = true;
  }

  deselectTask() {
    this.selectedTask = false;
  }

  get displayType(): Editor.Elements.DisplayType | null {
    // return INLINE / BLOCK
    return DISPLAY_TYPES.BLOCK;
  }

  get enclosedElementId() {
    return this.getAttribute('enclosed_element');
  }

  get selectableContent(): Node | null {
    return this;
  }

  get isEditable() {
    return EditorDOMElements.EDITABLE_LEVEL0_ELEMENTS.includes(this.tagName);
  }

  get isDeletable() {
    return EditorDOMElements.DELETABLE_LEVEL0_ELEMENTS.includes(this.tagName);
  }

  get isSplitable() {
    return EditorDOMElements.DEFAULT_SPLITABLE_LEVEL0_ELEMENTS.includes(this.tagName);
  }

  get isContentWrapper() {
    return EditorDOMElements.WRAPPER_LEVEL0_ELEMENTS.includes(this.tagName);
  }

  get splitFrom() {
    return this.dataset.splitFrom;
  }

  get splitIndex() {
    if (this.dataset.splitIndex) {
      return +this.dataset.splitIndex;
    } else if (this.viewModel) {
      this.viewModel.indexOfSplitView(this);
    }

    return -1;
  }

  isSplittedView() {
    return this.dataset.splitIndex != null;
  }

  isMainSplittedView() {
    return this.dataset.splitOriginal == 'true' && this.dataset.splitIndex == '0';
  }

  isEmpty() {
    let isEmpty = false;
    const queue: Node[] = [this];

    while (queue.length) {
      const element = queue.shift();

      if (element) {
        if (EditorDOMUtils.isEmptyElement(element)) {
          isEmpty = true;
          if (element.childNodes.length) {
            queue.unshift(...Array.from(element.childNodes));
          }
        } else {
          isEmpty = false;
          break;
        }
      }
    }

    return isEmpty;
  }

  // --------------------------------------------------
  //                Style Attributes
  // --------------------------------------------------
  get ALLOWED_STYLE_ATTRIBUTES(): Editor.Edition.BlockStyles[] {
    return [];
  }

  getAllowedStyleAttributes() {
    return this.ALLOWED_STYLE_ATTRIBUTES;
  }

  hasStyleAttribute(attribute: Editor.Edition.BlockStyles) {
    if (attribute != null && this.ALLOWED_STYLE_ATTRIBUTES.includes(attribute)) {
      return (
        this.dataset[attribute] != null &&
        this.dataset[attribute] !== 'null' &&
        this.dataset[attribute] !== ''
      );
    }
    return false;
  }

  getStyleAttribute(attribute: Editor.Edition.BlockStyles) {
    if (attribute != null && this.hasStyleAttribute(attribute)) {
      return this.dataset[attribute];
    }
    return undefined;
  }

  getStyleAttributes() {
    const styleAttributes: Editor.Edition.BlockStylesMap = {};

    let i;
    for (i = 0; i < this.ALLOWED_STYLE_ATTRIBUTES.length; i++) {
      const styleKey = this.ALLOWED_STYLE_ATTRIBUTES[i];
      const attribute = this.getStyleAttribute(styleKey);
      if (attribute != null && styleKey != null) {
        // @ts-expect-error
        styleAttributes[styleKey] = attribute;
      }
    }

    return styleAttributes;
  }

  removeStyleAttribute(attribute: Editor.Edition.BlockStyles, force: boolean = false) {
    if (attribute != null && (force || this.hasStyleAttribute(attribute))) {
      delete this.dataset[attribute];
    }
  }

  removeStyleAttributes(attributes: Editor.Edition.BlockStyles[] = []) {
    if (Array.isArray(attributes)) {
      let i;
      for (i = 0; i < attributes.length; i++) {
        this.removeStyleAttribute(attributes[i]);
      }
    }
  }

  addStyleAttribute(attribute: Editor.Edition.BlockStyles, value: string | boolean | null) {
    if (attribute != null && this.ALLOWED_STYLE_ATTRIBUTES.includes(attribute)) {
      if (value != null) {
        this.dataset[attribute] = `${value}`;
      } else {
        this.removeStyleAttribute(attribute, true);
      }
    }
  }

  protected onMouseEnter() {
    const widgetsManager = this.Visualizer?.getWidgetsManager?.();
    if (widgetsManager) {
      widgetsManager.addWidget('lockTooltip', this, {
        userId: this.userIdLocking,
        testId: this.id,
      });
    }
  }

  protected onMouseLeave() {
    const widgetsManager = this.Visualizer?.getWidgetsManager?.();
    if (widgetsManager) {
      widgetsManager.removeWidget('lockTooltip', this.id);
    }
  }

  private initializeLockTooltip() {
    const lock = this.getAttribute('lock');
    if (typeof lock === 'string' && lock !== 'true') {
      const [user] = lock.split('|') ?? [''];
      this.userIdLocking = user;
    }

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
  }
  private destroyLockTooltip() {
    this.userIdLocking = '';
    const widgetsManager = this.Visualizer?.getWidgetsManager?.();
    if (widgetsManager) {
      widgetsManager.removeWidget('lockTooltip', this.id);
    }
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
  }
}
